import React from "react";
import { registerExtension, EXTENSION_SORT_ORDER } from "@core/components/PipelineComponents/ExtensionProvider";
import { TooltipWrapper } from "@core/components/TooltipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { Switch } from "@core/components/ui/switch";
import { Label } from "@core/components/ui/label";
import { useApi } from "@core/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@core/components/ui/button";
import { EditorSection } from "@core/admin/components/settings/SettingsComponents";
import Spinner from "@core/components/ui/Spinner";

const PaymentsComponent = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  // Load payment method providers
  const { data: providersData, isLoading: providersLoading } = useQuery({
    queryKey: ["paymentMethodProviders"],
    queryFn: () => api.ListPaymentMethodProvider(),
  });

  // Load all setting groups to find provider plugin settings
  const { data: settingGroups, isLoading: settingsGroupsLoading } = useQuery({
    queryKey: ["settingGroups"],
    queryFn: () => api["/settings"](),
  });

  const providers = providersData?.Results ?? [];

  // Helper to map provider Key to plugin name
  const getPluginNameForProvider = (providerKey: string): string | null => {
    if (!settingGroups) return null;
    
    // Map provider keys to plugin names
    const pluginNameMap: Record<string, string> = {
      "Stripe": "Stripe Payments Provider Plugin",
      "Mock": "Mock Payments Plugin",
      "PayTrace": "PayTrace Payments",
    };
    
    return pluginNameMap[providerKey] || null;
  };

  // Helper to get settings group name for a provider
  const getSettingsGroupForProvider = (pluginName: string): string | null => {
    if (!settingGroups || !settingGroups[pluginName]) return null;
    
    // Usually the settings group matches the pattern: {Provider}PaymentSettings
    // e.g., "StripePaymentSettings", "MockPaymentSettings"
    const groups = settingGroups[pluginName];
    // Return the first group, or find one matching the pattern
    return groups.find(g => g.includes("PaymentSettings")) || groups[0] || null;
  };

  // Mutation to update provider IsActive status
  // When activating a provider, automatically deactivates the previously active one
  const updateProviderMutation = useMutation({
    mutationFn: async ({ key, isActive, allProviders }: { key: string; isActive: boolean; allProviders: typeof providers }) => {
      if (isActive) {
        // When activating, first deactivate all other providers
        const otherActiveProviders = allProviders.filter(
          (p) => p.Key !== key && p.IsActive === true
        );
        
        // Deactivate all other active providers
        await Promise.all(
          otherActiveProviders.map((provider) =>
            api.UpdatePaymentMethodProvider({
              Key: provider.Key,
              IsActive: false,
            })
          )
        );
      }
      
      // Update the selected provider
      await api.UpdatePaymentMethodProvider({
        Key: key,
        IsActive: isActive,
      });
    },
    onSuccess: () => {
      // Invalidate providers list
      queryClient.invalidateQueries({ queryKey: ["paymentMethodProviders"] });
      // Invalidate setting groups so newly active providers' settings are included
      queryClient.invalidateQueries({ queryKey: ["settingGroups"] });
      // Invalidate all provider settings queries to ensure fresh data when switching
      queryClient.invalidateQueries({ queryKey: ["providerSettings"] });
    },
  });

  if (providersLoading || settingsGroupsLoading) {
    return <div>Loading...</div>;
  }

  if (providers.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Payments</h3>
        <p className="text-sm text-muted-foreground">No payment providers configured.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Payments</h3>
      <div className="space-y-4">
        {providers.map((provider) => {
          const pluginName = getPluginNameForProvider(provider.Key);
          const settingsGroup = pluginName ? getSettingsGroupForProvider(pluginName) : null;
          
          return (
            <ProviderToggle
              key={provider.Key}
              provider={provider}
              allProviders={providers}
              updateProviderMutation={updateProviderMutation}
              pluginName={pluginName}
              settingsGroup={settingsGroup}
            />
          );
        })}
      </div>
    </div>
  );
};

type Provider = { Key: string; Name?: string | null; IsActive?: boolean | null };

interface ProviderToggleProps {
  provider: Provider;
  allProviders: Provider[];
  updateProviderMutation: {
    mutate: (variables: { key: string; isActive: boolean; allProviders: Provider[] }) => void;
    isPending: boolean;
  };
  pluginName: string | null;
  settingsGroup: string | null;
}

const ProviderToggle = ({ 
  provider, 
  allProviders, 
  updateProviderMutation,
  pluginName,
  settingsGroup 
}: ProviderToggleProps) => {
  const [isActive, setIsActive] = React.useState<boolean>(provider.IsActive ?? false);
  const api = useApi();
  const queryClient = useQueryClient();

  // Load settings for this provider if plugin name and group are available
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ["providerSettings", provider.Key, pluginName, settingsGroup],
    queryFn: () => {
      if (!pluginName || !settingsGroup) return null;
      return api["/settings/{plugin}/{group}"](pluginName, settingsGroup);
    },
    enabled: !!pluginName && !!settingsGroup && isActive,
  });

  const methods = useForm<any>({ 
    defaultValues: settings?.SettingValues ?? {} 
  });

  // Update form when settings load
  React.useEffect(() => {
    if (settings?.SettingValues) {
      methods.reset(settings.SettingValues);
    }
  }, [settings, methods]);

  // Mutation to save settings
  const saveSettingsMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!pluginName || !settingsGroup) return;
      await api["/settings/{plugin}/{group}/update"](pluginName, settingsGroup, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["providerSettings", provider.Key, pluginName, settingsGroup] });
    },
  });

  // Update local state when provider data changes
  React.useEffect(() => {
    if (provider.IsActive !== undefined) {
      setIsActive(provider.IsActive);
    }
  }, [provider.IsActive]);

  const handleToggle = (checked: boolean) => {
    setIsActive(checked);
    updateProviderMutation.mutate({ key: provider.Key, isActive: checked, allProviders });
  };

  // Refetch settings when provider becomes active
  // This effect ensures we fetch the correct provider's settings when switching
  React.useEffect(() => {
    if (isActive && pluginName && settingsGroup && provider.Key) {
      // Force refetch settings for this specific provider when it becomes active
      // Using provider.Key ensures we refetch the correct provider's settings even if
      // pluginName/settingsGroup props haven't updated yet
      queryClient.refetchQueries({ 
        queryKey: ["providerSettings", provider.Key] 
      });
    }
  }, [isActive, provider.Key, pluginName, settingsGroup, queryClient]);

  const onSubmit = (data: any) => {
    saveSettingsMutation.mutate(data);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <Switch
          id={`provider-${provider.Key}`}
          checked={isActive}
          onCheckedChange={handleToggle}
          disabled={updateProviderMutation.isPending}
        />
        <div className="flex items-center gap-2">
          <Label htmlFor={`provider-${provider.Key}`} className="cursor-pointer">
            {provider.Name || provider.Key}
          </Label>
          <TooltipWrapper
            tooltipContent={`Enable the ${provider.Name || provider.Key} payment provider`}
          >
            <FontAwesomeIcon
              icon={faQuestionCircle}
              className="text-muted-foreground cursor-help"
            />
          </TooltipWrapper>
        </div>
      </div>
      
      {/* Automatically generated settings form - only show when active */}
      {isActive && (
        <div className="ml-8 pl-4 border-l-2 border-muted">
          {!pluginName || !settingsGroup ? (
            // No settings available (e.g., Mock provider)
            <div className="text-sm text-muted-foreground">
              {provider.Key === "Mock" 
                ? "Mock Payment Plugin allows you to test the payment process without using a real payment provider. No configuration is needed."
                : `Settings not available for ${provider.Name || provider.Key}`}
            </div>
          ) : settingsLoading ? (
            <div className="text-sm text-muted-foreground">Loading settings...</div>
          ) : settings ? (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
                {settings.SettingProperties.map((s) => (
                  <EditorSection key={s.Name} setting={s} />
                ))}
                <Button type="submit" disabled={saveSettingsMutation.isPending}>
                  {saveSettingsMutation.isPending ? <Spinner /> : `Save ${provider.Name || provider.Key} Settings`}
                </Button>
              </form>
            </FormProvider>
          ) : (
            <div className="text-sm text-muted-foreground">
              Settings not available for {provider.Name || provider.Key}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

registerExtension(
  "admin.system.sitesetup.siteconfiguration",
  PaymentsComponent,
  "Payments",
  false,
  EXTENSION_SORT_ORDER.HIGH + 100
);
